/**
 * NotificationSettings Component
 * UI for managing push notification preferences with per-event toggles
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useTranslation } from '../contexts/LanguageContext';
import './NotificationSettings.css';

// Default notification preferences
const DEFAULT_PREFERENCES = {
  yourTurn: true,       // Always recommended to keep on
  matchFound: true,     // Game invites
  friendRequests: true, // Friend requests
  messages: true,       // Chat messages
  spectatorRequests: true, // Spectator requests
  tournaments: true,    // Upcoming tournaments
};

const NotificationSettings = () => {
  const { t } = useTranslation();
  const {
    permission,
    soundEnabled,
    isEnabled,
    requestPermission,
    toggleSound,
    testNotification
  } = useNotifications();
  
  const [testing, setTesting] = useState(false);
  
  // Per-event notification preferences
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('notificationPreferences');
    if (saved) {
      try {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) };
      } catch (e) {
        return DEFAULT_PREFERENCES;
      }
    }
    return DEFAULT_PREFERENCES;
  });

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
    // Dispatch event for other components to listen
    window.dispatchEvent(new CustomEvent('notificationPreferencesChanged', { detail: preferences }));
  }, [preferences]);

  const togglePreference = useCallback((key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const handleRequestPermission = async () => {
    const result = await requestPermission();
    if (result === 'denied') {
      alert(t('notificationSettings.permissionDeniedAlert', 'Notifications were blocked. Please enable them in your browser settings.'));
    }
  };

  const handleTest = async () => {
    setTesting(true);
    await testNotification();
    setTimeout(() => setTesting(false), 1000);
  };

  const getPermissionStatus = () => {
    switch (permission) {
      case 'granted':
        return { text: t('notificationSettings.enabled', 'Enabled'), icon: '✅', color: '#10b981' };
      case 'denied':
        return { text: t('notificationSettings.blocked', 'Blocked'), icon: '🚫', color: '#ef4444' };
      default:
        return { text: t('notificationSettings.notSet', 'Not Set'), icon: '❓', color: '#f59e0b' };
    }
  };

  const status = getPermissionStatus();

  // Event configuration for the toggleable notifications
  const notificationEvents = [
    {
      key: 'yourTurn',
      icon: '♟️',
      name: t('notificationSettings.yourTurn', 'Your Turn'),
      description: t('notificationSettings.yourTurnDesc', 'When it\'s your turn to move'),
      required: true, // This one should always be on for good UX
    },
    {
      key: 'matchFound',
      icon: '🎮',
      name: t('notificationSettings.gameInvites', 'Game Invites'),
      description: t('notificationSettings.gameInvitesDesc', 'When a match is found or you receive invites'),
    },
    {
      key: 'friendRequests',
      icon: '👋',
      name: t('notificationSettings.friendRequests', 'Friend Requests'),
      description: t('notificationSettings.friendRequestsDesc', 'When someone sends you a friend request'),
    },
    {
      key: 'messages',
      icon: '💬',
      name: t('notificationSettings.messages', 'Messages'),
      description: t('notificationSettings.messagesDesc', 'When you receive chat messages'),
    },
    {
      key: 'spectatorRequests',
      icon: '👁️',
      name: t('notificationSettings.spectatorRequests', 'Spectator Requests'),
      description: t('notificationSettings.spectatorRequestsDesc', 'When someone wants to watch your game'),
    },
    {
      key: 'tournaments',
      icon: '🏆',
      name: t('notificationSettings.tournaments', 'Tournaments'),
      description: t('notificationSettings.tournamentsDesc', 'Upcoming tournament reminders and updates'),
    },
  ];

  return (
    <div className="notification-settings" data-testid="notification-settings">
      <h3 className="settings-section-title">
        🔔 {t('notificationSettings.title', 'Push Notifications')}
      </h3>
      
      <div className="notification-status">
        <span className="status-label">{t('notificationSettings.browserNotifications', 'Browser Notifications')}:</span>
        <span className="status-value" style={{ color: status.color }}>
          {status.icon} {status.text}
        </span>
      </div>

      {!isEnabled && permission !== 'denied' && (
        <div className="notification-prompt">
          <p>{t('notificationSettings.enablePrompt', 'Enable push notifications to get alerts when:')}</p>
          <ul>
            <li>♟️ {t('notificationSettings.promptTurn', 'It\'s your turn to move')}</li>
            <li>🎮 {t('notificationSettings.promptMatch', 'A match is found')}</li>
            <li>👋 {t('notificationSettings.promptFriend', 'You receive friend requests')}</li>
            <li>💬 {t('notificationSettings.promptMessages', 'You get new messages')}</li>
          </ul>
          <button 
            className="btn btn-primary enable-notifications-btn"
            onClick={handleRequestPermission}
            data-testid="enable-notifications-btn"
          >
            🔔 {t('notificationSettings.enableButton', 'Enable Notifications')}
          </button>
        </div>
      )}

      {permission === 'denied' && (
        <div className="notification-blocked-info">
          <p>⚠️ {t('notificationSettings.blockedMessage', 'Notifications are blocked by your browser.')}</p>
          <p className="small-text">
            {t('notificationSettings.blockedHelp', 'To enable them, click the lock/info icon in your browser\'s address bar and allow notifications for this site.')}
          </p>
        </div>
      )}

      <div className="notification-option">
        <label className="toggle-label">
          <span className="option-text">
            🔊 {t('notificationSettings.sounds', 'Notification Sounds')}
          </span>
          <div className="toggle-switch">
            <input
              type="checkbox"
              checked={soundEnabled}
              onChange={(e) => toggleSound(e.target.checked)}
              data-testid="sound-toggle"
            />
            <span className="toggle-slider"></span>
          </div>
        </label>
        <p className="option-description">
          {t('notificationSettings.soundsDesc', 'Play sounds for important events like your turn')}
        </p>
      </div>

      {isEnabled && (
        <div className="notification-test">
          <button 
            className="btn btn-secondary test-btn"
            onClick={handleTest}
            disabled={testing}
            data-testid="test-notification-btn"
          >
            {testing ? '🔔 ' + t('notificationSettings.sent', 'Sent!') : '🧪 ' + t('notificationSettings.testButton', 'Test Notification')}
          </button>
        </div>
      )}

      <div className="notification-events">
        <h4>{t('notificationSettings.eventPreferences', 'Event Preferences')}</h4>
        <p className="events-description">
          {t('notificationSettings.eventPreferencesDesc', 'Choose which events you want to be notified about')}
        </p>
        <div className="event-list">
          {notificationEvents.map((event) => (
            <div key={event.key} className="event-item-toggle" data-testid={`notification-toggle-${event.key}`}>
              <div className="event-info">
                <span className="event-icon">{event.icon}</span>
                <div className="event-details">
                  <span className="event-name">{event.name}</span>
                  <span className="event-description">{event.description}</span>
                </div>
              </div>
              <div className="event-toggle">
                {event.required ? (
                  <span className="event-required" title={t('notificationSettings.alwaysOn', 'Always on for best experience')}>
                    {t('notificationSettings.alwaysOnLabel', 'Always On')}
                  </span>
                ) : (
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences[event.key]}
                      onChange={() => togglePreference(event.key)}
                      data-testid={`toggle-${event.key}`}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
